import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { useIntl } from '../../../util/reactIntl';
import { trackEvent } from '../../../analytics/trackEvent';
import {
  AvatarMedium,
  InlineTextButton,
  Modal,
  ResponsiveImage,
  SecondaryButton,
} from '../../../components';

import { SHARE_TARGETS, shareUrlFor } from './shareTargets';
import {
  IconFacebookMark,
  IconMail,
  IconMessengerMark,
  IconPinterestMark,
  IconWhatsAppMark,
  IconXMark,
} from './icons';
import css from './ShareListing.module.css';

const TARGET_ICONS = {
  facebook: IconFacebookMark,
  messenger: IconMessengerMark,
  whatsapp: IconWhatsAppMark,
  x: IconXMark,
  pinterest: IconPinterestMark,
  email: IconMail,
};

// How long "Lenken er kopiert!" stays up.
const COPIED_NOTICE_MS = 2000;

/**
 * Copy text to the clipboard. The async API needs a secure context and a
 * user gesture (we have both here); the execCommand path is the fallback for
 * the odd browser that still lacks it.
 */
const copyToClipboard = async text => {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const el = document.createElement('textarea');
  el.value = text;
  el.setAttribute('readonly', '');
  el.style.position = 'absolute';
  el.style.left = '-9999px';
  document.body.appendChild(el);
  el.select();
  document.execCommand('copy');
  document.body.removeChild(el);
};

/**
 * Desktop share dialog — the fallback when there is no OS share sheet to
 * hand off to. A small preview of the thing being shared (what the recipient
 * will see), the common platforms, and copy-link. Takes the same `share` and
 * `preview` as ShareButton; see shareProps.js.
 *
 * Every platform opens its official share endpoint in a new tab from inside
 * the click handler, so popup blockers leave it alone. Email is a mailto:
 * and goes to the mail client instead of a tab.
 */
const ShareModal = props => {
  const {
    isOpen,
    onClose,
    onManageDisableScrolling,
    share,
    preview,
    modalTitle,
    facebookAppId,
  } = props;
  const intl = useIntl();
  const [copied, setCopied] = useState(false);
  const copiedTimer = useRef(null);
  useEffect(() => () => window.clearTimeout(copiedTimer.current), []);

  const { url: shareUrl, title, text: shareText, contentType, itemId } = share || {};
  const { image, imageAlt, variants = [], avatarUser, subtitle, detail } = preview || {};
  const previewTitle = preview?.title || title;
  // Pinterest wants a picture; the largest variant the preview was given.
  const imageUrl =
    [...variants].reverse().map(v => image?.attributes?.variants?.[v]?.url).find(Boolean) ||
    null;

  const openTarget = target => {
    const href = shareUrlFor(target, {
      url: shareUrl,
      title,
      text: shareText,
      imageUrl,
      facebookAppId,
    });
    if (!href) {
      return;
    }
    trackEvent('share', { method: target, content_type: contentType, item_id: itemId });
    if (target === 'email') {
      window.location.href = href;
    } else {
      window.open(href, '_blank', 'noopener,noreferrer');
    }
  };

  const handleCopy = async () => {
    try {
      await copyToClipboard(shareUrl);
      trackEvent('share', { method: 'copy_link', content_type: contentType, item_id: itemId });
      setCopied(true);
      window.clearTimeout(copiedTimer.current);
      copiedTimer.current = window.setTimeout(() => setCopied(false), COPIED_NOTICE_MS);
    } catch (e) {
      // Nothing sensible to show — the link is on screen in the address
      // bar, and a failed copy is the browser refusing, not a bug of ours.
    }
  };

  return (
    <Modal
      id="ListingPage.share"
      isOpen={isOpen}
      onClose={onClose}
      usePortal
      onManageDisableScrolling={onManageDisableScrolling}
      contentClassName={css.modalContent}
    >
      <h2 className={css.modalTitle}>{modalTitle}</h2>

      <div className={css.preview}>
        <div className={classNames(css.previewImage, { [css.previewAvatar]: !!avatarUser })}>
          {avatarUser ? (
            // A profile previews with its avatar, which falls back to initials
            // when there is no photo, so this is never an empty box.
            <AvatarMedium className={css.previewAvatarImg} user={avatarUser} disableProfileLink />
          ) : image ? (
            <ResponsiveImage
              rootClassName={css.previewImg}
              image={image}
              alt={imageAlt || previewTitle}
              variants={variants}
            />
          ) : null}
        </div>
        <div className={css.previewText}>
          <p className={css.previewTitle}>{previewTitle}</p>
          {subtitle ? <p className={css.previewBrand}>{subtitle}</p> : null}
          {detail ? <p className={css.previewPrice}>{detail}</p> : null}
        </div>
      </div>

      <ul className={css.targets}>
        {SHARE_TARGETS.filter(t => t !== 'messenger' || !!facebookAppId).map(target => {
          const Icon = TARGET_ICONS[target];
          const platform = intl.formatMessage({ id: `ListingPage.shareTarget.${target}` });
          return (
            <li key={target} className={css.targetItem}>
              <InlineTextButton
                rootClassName={classNames(css.target, css[`target_${target}`])}
                type="button"
                onClick={() => openTarget(target)}
                aria-label={intl.formatMessage({ id: 'ListingPage.shareOpenTarget' }, { platform })}
                data-share-target={target}
              >
                <span className={css.targetIcon}>
                  <Icon className={css.targetSvg} />
                </span>
                <span className={css.targetLabel}>{platform}</span>
              </InlineTextButton>
            </li>
          );
        })}
      </ul>

      <div className={css.copyRow}>
        <SecondaryButton type="button" className={css.copyButton} onClick={handleCopy}>
          {intl.formatMessage({ id: 'ListingPage.shareCopyLink' })}
        </SecondaryButton>
        {copied ? (
          <p className={css.copied} role="status">
            {intl.formatMessage({ id: 'ListingPage.shareLinkCopied' })}
          </p>
        ) : null}
      </div>
    </Modal>
  );
};

export default ShareModal;
